import * as React from 'react';
import { sortPartialDate, isPartialDate } from 'neuro-utils';

import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { fm } from 'Components/FormatMessage';
import { getSessionType, headerText } from 'Utility/ninmtUtil';
import { DescriptionText } from 'Components/DashboardTile/TileContentMaker/components/common/commonComponents';
import { FormattedMessage } from 'react-intl';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType } from 'Utility/documentHandling';

const TDCSData = ({ document }: { document: IDocumentWithTypeV2<'tdcs'> }): React.JSX.Element => {
  const { sessions: _sessions, unusedSessions: _unusedSessions, otherEvents: _otherEvents } = document;
  const [sessions, unusedSessions, otherEvents] = [_sessions ?? [], _unusedSessions ?? [], _otherEvents ?? []];

  const allEvents = [...sessions, ...unusedSessions, ...otherEvents];

  const sessionCount = Array.isArray(sessions) ? sessions.length : '-';

  const isOtherEvent = (event: (typeof allEvents)[0]): event is (typeof otherEvents)[0] => {
    if (otherEvents.some(s => s.id === event.id)) return true;
    return false;
  };

  return (
    <React.Fragment>
      <TileContentMaker
        type="bigHeaderInfo"
        bigHeaderText={
          document.type
            ? fm(`tdcs.opts.${document.type}`)
            : !document.hasEnded
              ? fm('tdcs.tdcsOngoing')
              : fm('tdcs.tdcsHasEnded')
        }
        bigHeaderValue={headerText(document)}
        data={
          document.sessions && Array.isArray(sessions) && document.hasEnded
            ? [
                {
                  title: fm('tdcs.sessionsAndProtocols'),
                  value: <div style={{ whiteSpace: 'pre-line' }}>{sessionCount}</div>,
                  key: 'sessions',
                },
              ]
            : []
        }
      />
      {!document.hasEnded && (
        <TileContentMaker
          type="specificList"
          itemsCanFitAmount={5}
          data={allEvents.map(e => {
            const eventType = isOtherEvent(e) ? e.type : getSessionType(e, sessions, unusedSessions);
            return {
              date: isPartialDate(e.date) ? formatPartialDate(e.date) : '',
              title: eventType ? fm(`tdcs.opts.eventTypes.${eventType}`) : fm('tdcs.session'),
              value: null,
              key: e.id,
            };
          })}
        />
      )}
    </React.Fragment>
  );
};

const TDCSDash = ({ documents }: IOwnProps): React.JSX.Element | undefined => {
  const sortedTdcs = filterByDocumentType(documents, 'tdcs')
    .sort((n1, n2) => n1._cdate - n2._cdate)
    .sort((n1, n2) => sortPartialDate(n2.date, n1.date));

  const sortedNinmtTreatmentPeriod = filterByDocumentType(documents, 'ninmtTreatmentPeriod')
    .sort((n1, n2) => n1._cdate - n2._cdate)
    .sort((n1, n2) => sortPartialDate(n2.date, n1.date));

  const latestTDCS = sortedTdcs.find(d => !d.hasEnded) ?? (sortedTdcs.length > 0 ? sortedTdcs[0] : undefined);

  return (
    <React.Fragment>
      {latestTDCS ? (
        sortPartialDate(sortedNinmtTreatmentPeriod[0]?.date, latestTDCS.date) === 1 ? (
          <DescriptionText>
            <FormattedMessage id="tdcs.noActiveTreatmentPeriod" />
          </DescriptionText>
        ) : (
          <TDCSData document={latestTDCS} />
        )
      ) : (
        <></>
      )}
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<
    'tdcs' | 'doctorsOrders' | 'ninmtTreatmentPeriod' | 'contraIndicationsToTreatment' | 'symptomsAndLocations'
  >[];
}

export default TDCSDash;
